'use client';

import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Box, VStack, HStack } from '@terra/ui-styled-system';
import {
  Text,
  Input,
  Scroll,
  Spinner,
  IconButton,
} from '@terra/ui-design-system';
import {
  LinkSquare02Icon,
  GitPullRequestIcon,
  Search01Icon,
} from '@terra/ui-icons';
import type { ApplicationEntryPoint } from '@terra/shared-schemas-git';
import { terraProxyApiClient } from '../../clients/testerClient';
import { useAppTargetId } from '../../store/projectStore';
import {
  CodeChangeGraphDrawer,
  type EntryPoint,
} from './CodeChangeGraphDrawer';
import { EntryPointCard } from './EntryPointCard';

interface EntryPointsListProps {
  endpointId?: string;
  onViewGraph: () => void;
  onEntryPointSelect?: (entryPointId: string) => void;
}
type GroupedEntryPointFromAPI = {
  pullNumber: number;
  prOwner: string;
  ownerAvatar: string;
  prTitle: string;
  prUrl: string;
  createdAt: string | Date;
  entryPoints: Array<{
    fileName?: string | null;
    description?: string | null;
    codeSnippet?: string | null;
    entryPoint?: ApplicationEntryPoint['entryPoint'] | null;
    id: string;
    investigated?: boolean | null;
    isReachable?: boolean | null;
  }>;
};
type GroupedEntryPoint = {
  pullNumber: number;
  prOwner: string;
  ownerAvatar: string;
  prTitle: string;
  prUrl: string;
  createdAt: Date;
  entryPoints: Array<{
    fileName?: string | null;
    description?: string | null;
    codeSnippet?: string | null;
    entryPoint?: ApplicationEntryPoint['entryPoint'] | null;
    id: string;
    investigated?: boolean | null;
    isReachable?: boolean | null;
  }>;
};

export function EntryPointsList({ endpointId }: EntryPointsListProps) {
  const appTargetId = useAppTargetId();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedEntryPoint, setSelectedEntryPoint] =
    useState<EntryPoint | null>(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const { data: entryPointsData, isLoading } = useQuery({
    queryKey: ['entry-points', appTargetId, endpointId],
    queryFn: async () => {
      if (!appTargetId) return null;

      return terraProxyApiClient.getApplicationEntryPoints(
        appTargetId,
        endpointId ? { endpointId } : undefined,
      );
    },
    enabled: !!appTargetId,
    refetchOnWindowFocus: false,
    staleTime: 30000,
  });
  const groupedEntryPoints = useMemo<GroupedEntryPoint[]>(() => {
    if (!entryPointsData?.entryPoints) return [];

    return entryPointsData.entryPoints.map(
      (group: GroupedEntryPointFromAPI) => ({
        ...group,
        createdAt: new Date(group.createdAt),
      }),
    );
  }, [entryPointsData]);
  // Filter and flatten entry points
  const filteredEntryPoints = useMemo(() => {
    if (!searchTerm) return groupedEntryPoints;
    const searchLower = searchTerm.toLowerCase();

    return groupedEntryPoints
      .map((group) => ({
        ...group,
        entryPoints: group.entryPoints.filter((ep) => {
          const fileNameMatch = ep.fileName
            ? ep.fileName.toLowerCase().includes(searchLower)
            : false;
          const descriptionMatch = ep.description
            ? ep.description.toLowerCase().includes(searchLower)
            : false;

          return fileNameMatch || descriptionMatch;
        }),
      }))
      .filter((group) => group.entryPoints.length > 0);
  }, [groupedEntryPoints, searchTerm]);
  const formatDate = (date: Date): string => {
    const now = new Date();
    const diffDays = Math.floor(
      (now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24),
    );

    if (diffDays === 0) return 'today';
    if (diffDays === 1) return '1d ago';
    if (diffDays < 7) return `${diffDays}d ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)}w ago`;

    return `${Math.floor(diffDays / 30)}mo ago`;
  };

  const handleViewGraph = (entryPoint: EntryPoint) => {
    setSelectedEntryPoint(entryPoint);
    setIsDrawerOpen(true);
  };

  return (
    <VStack gap={0} alignItems="stretch" h="full" overflow="hidden">
      {/* Search */}
      <Box px={2} py={2} borderBottomWidth="1px" borderColor="border.subtle">
        <Box position="relative">
          <Input
            placeholder="Search..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            size="xs"
            fontSize="xs"
          />
          <Box
            position="absolute"
            right="2"
            top="50%"
            transform="translateY(-50%)"
            pointerEvents="none"
          >
            <Search01Icon size={12} color="var(--colors-fg-muted)" />
          </Box>
        </Box>
      </Box>

      {/* Content */}
      <Box flex="1" overflowY="auto" overflowX="hidden">
        {isLoading ? (
          <Box
            h="full"
            display="flex"
            alignItems="center"
            justifyContent="center"
          >
            <Spinner size="sm" />
          </Box>
        ) : filteredEntryPoints.length === 0 ? (
          <Box p={4}>
            <Text fontSize="xs" color="fg.muted" textAlign="center">
              {searchTerm ? 'No matches found' : 'No code changes'}
            </Text>
          </Box>
        ) : (
          <Scroll h="full">
            <VStack gap={0} alignItems="stretch">
              {filteredEntryPoints.map((group) => (
                <Box key={`pr-${group.pullNumber}`}>
                  {/* PR Header - More compact */}
                  <HStack
                    gap={2}
                    px={3}
                    py={1.5}
                    alignItems="center"
                    bg="bg.subtle"
                    borderBottomWidth="1px"
                    borderColor="border.subtle"
                  >
                    <GitPullRequestIcon
                      size={12}
                      color="var(--colors-fg-muted)"
                    />
                    <Text fontSize="2xs" fontWeight="medium" flex="1" truncate>
                      #{group.pullNumber} · {group.prTitle}
                    </Text>
                    <Text fontSize="2xs" color="fg.muted">
                      {formatDate(group.createdAt)}
                    </Text>
                    <IconButton
                      size="xs"
                      variant="ghost"
                      onClick={() => window.open(group.prUrl, '_blank')}
                      aria-label="Open PR"
                    >
                      <LinkSquare02Icon size={10} />
                    </IconButton>
                  </HStack>

                  {/* Entry Points - Tighter spacing */}
                  <VStack gap={0} alignItems="stretch">
                    {group.entryPoints.map((ep, index) => (
                      <Box
                        key={ep.id}
                        borderBottomWidth={
                          index < group.entryPoints.length - 1 ? '1px' : '0'
                        }
                        borderColor="border.subtle"
                      >
                        <EntryPointCard
                          entryPoint={ep}
                          onViewGraph={handleViewGraph}
                        />
                      </Box>
                    ))}
                  </VStack>
                </Box>
              ))}
            </VStack>
          </Scroll>
        )}
      </Box>

      {/* Code Change Graph Drawer */}
      <CodeChangeGraphDrawer
        entryPoint={selectedEntryPoint}
        isOpen={isDrawerOpen}
        onClose={() => {
          setIsDrawerOpen(false);
          setSelectedEntryPoint(null);
        }}
      />
    </VStack>
  );
}
